import React from 'react';
import type { NormalizedIdeaCardData } from '@/lib/idea-card-types';
import { getMarkdownIdeaComponents } from './IdeaCards/markdown-components';
import { MarkdownRenderer, type MarkdownShellTokens } from './markdown/primitives';
import { assistantMessageMarkdownShell } from './markdown/tokens';
import { useTranslation } from 'react-i18next';
import { HorizontalCardGroup } from './HorizontalCardGroup';

// Preview types that require full-width rendering and should be isolated from other cards
const FULL_WIDTH_PREVIEW_TYPES = new Set(['calendar', 'comparison_view']);

interface InlineCardsRendererProps {
  content: string;
  cards: NormalizedIdeaCardData[];
  markdownComponents: Record<string, React.ComponentType<any>>;
  shell?: MarkdownShellTokens;
  markdownShell?: MarkdownShellTokens;
}

/**
 * Renders markdown content with inline horizontal cards.
 * Replaces [HORIZONTAL_CARD:n] placeholders with actual card components.
 */
export const InlineCardsRenderer: React.FC<InlineCardsRendererProps> = ({
  content,
  cards,
  markdownComponents,
  shell,
  markdownShell,
}) => {
  const { t } = useTranslation();
  const resolvedShell = shell ?? markdownShell ?? assistantMessageMarkdownShell;

  const markdownIdeaComponentsByDensity = React.useMemo(
    () => ({
      compact: getMarkdownIdeaComponents((text: string) => [text], t, { density: 'compact' }),
      relaxed: getMarkdownIdeaComponents((text: string) => [text], t, { density: 'relaxed' }),
    }),
    [t]
  );

  // Split content by placeholder pattern and group consecutive horizontal cards
  const parts = React.useMemo(() => {
    const placeholderPattern = /\[HORIZONTAL_CARD:(\d+)\]/g;
    const segments: Array<{ type: 'text' | 'card'; content: string; cardIndex?: number }> = [];
    let lastIndex = 0;
    let match;

    while ((match = placeholderPattern.exec(content)) !== null) {
      // Add text before placeholder
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: content.substring(lastIndex, match.index),
        });
      }

      // Add card placeholder
      segments.push({
        type: 'card',
        content: match[0],
        cardIndex: parseInt(match[1], 10),
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      segments.push({
        type: 'text',
        content: content.substring(lastIndex),
      });
    }

    // Group consecutive horizontal cards
    // Cards with the same groupLabel are grouped together
    // Cards with no groupLabel but consecutive positions are also grouped
    // Cards with special preview types (comparison_view, calendar) are separated into their own groups
    const result: Array<{ type: 'text' | 'cardGroup'; content?: string; cardIndices?: number[]; groupLabel?: string }> = [];
    let currentCardGroup: number[] | null = null;
    let currentGroupLabel: string | undefined = undefined;
    let currentPreviewType: string | undefined = undefined;

    segments.forEach((segment) => {
      if (segment.type === 'text') {
        // Check if text is just whitespace - if so, don't break the card group
        const trimmedContent = segment.content?.trim();
        if (!trimmedContent) {
          // Skip empty text segments between cards
          return;
        }

        // Flush current card group if any
        if (currentCardGroup && currentCardGroup.length > 0) {
          result.push({ type: 'cardGroup', cardIndices: currentCardGroup, groupLabel: currentGroupLabel });
          currentCardGroup = null;
          currentGroupLabel = undefined;
          currentPreviewType = undefined;
        }
        result.push({ type: 'text', content: segment.content });
      } else if (segment.type === 'card' && segment.cardIndex !== undefined) {
        const card = cards[segment.cardIndex];

        // If card doesn't exist, skip it
        if (!card) {
          console.warn(`[InlineCardsRenderer] Card at index ${segment.cardIndex} is missing`);
          return;
        }

        // For inline rendering, treat all cards as horizontal and group them
        const cardGroupLabel = card.groupLabel;
        const cardPreviewType = card.preview?.type;
        const hasSpecialPreview = cardPreviewType && FULL_WIDTH_PREVIEW_TYPES.has(cardPreviewType);

        // Determine if we should start a new group
        // Start new group if:
        // 1. There's a groupLabel mismatch (including undefined transitions)
        // 2. Preview type changes (especially when one has special preview and the other doesn't)
        // 3. Current card has a special preview type (these should be in their own groups)
        const shouldStartNewGroup =
          currentCardGroup !== null && (
            (currentGroupLabel !== cardGroupLabel) ||
            (currentPreviewType !== cardPreviewType) ||
            (hasSpecialPreview)
          );

        if (currentCardGroup === null || shouldStartNewGroup) {
          // Flush previous group if any
          if (currentCardGroup && currentCardGroup.length > 0) {
            result.push({ type: 'cardGroup', cardIndices: currentCardGroup, groupLabel: currentGroupLabel });
          }
          currentCardGroup = [segment.cardIndex];
          currentGroupLabel = cardGroupLabel;
          currentPreviewType = cardPreviewType;
        } else {
          currentCardGroup.push(segment.cardIndex);
        }
      }
    });

    // Flush final card group if any
    if (currentCardGroup && currentCardGroup.length > 0) {
      result.push({ type: 'cardGroup', cardIndices: currentCardGroup, groupLabel: currentGroupLabel });
    }

    return result;
  }, [content, cards]);

  return (
    <>
      {parts.map((part, idx) => {
        if (part.type === 'text') {
          const trimmedContent = part.content?.trim();
          if (!trimmedContent) return null;

          return (
            <MarkdownRenderer
              key={idx}
              content={trimmedContent}
              components={markdownComponents}
              shell={resolvedShell}
            />
          );
        }

        // Render card group
        if (part.type === 'cardGroup' && part.cardIndices) {
          const groupCards = part.cardIndices
            .map(idx => cards[idx])
            .filter((card): card is NormalizedIdeaCardData => card !== undefined)
            // Force layout to 'horizontal' for inline rendering so titles are displayed
            .map(card => ({ ...card, layout: 'horizontal' as const }));

          if (groupCards.length === 0) return null;

          // For inline rendering context, always render as horizontal cards
          // regardless of the original layout property
          return (
            <HorizontalCardGroup
              key={idx}
              groupLabel={part.groupLabel}
              cards={groupCards}
              markdownIdeaComponentsByDensity={markdownIdeaComponentsByDensity}
            />
          );
        }

        return null;
      })}
    </>
  );
};
